import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Alert, Badge, Modal, ListGroup, Tabs, Tab } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import RichTextEditor from '../../components/RichTextEditor';
import { marked } from 'marked';

const ContentMatrix = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // IP and content types
  const [frameworks, setFrameworks] = useState([]);
  const [contentTypes, setContentTypes] = useState({});
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [selectedType, setSelectedType] = useState('');

  // Content generation flow
  const [view, setView] = useState('library'); // library, select-ip, ideas, generate, edit
  const [ideas, setIdeas] = useState([]);
  const [selectedIdea, setSelectedIdea] = useState(null);

  // Content library
  const [contentPieces, setContentPieces] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState({});
  const [hashtagsInput, setHashtagsInput] = useState('');

  // Repurpose modal
  const [showRepurpose, setShowRepurpose] = useState(false);
  const [repurposeType, setRepurposeType] = useState('');

  // Regenerate feedback
  const [regenerateFeedback, setRegenFeedback] = useState('');
  const [regenOptions, setRegenOptions] = useState({
    title: false,
    hook: false,
    content: true,  // Content checked by default
    cta: false
  });

  // Social posting
  const [showPostModal, setShowPostModal] = useState(false);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postContent, setPostContent] = useState('');

  // Tab control
  const [activeTab, setActiveTab] = useState('content');

  // CTA options
  const [ctaOptions, setCtaOptions] = useState([]);
  const [selectedCta, setSelectedCta] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [frameworksRes, typesRes, contentRes, ctaRes] = await Promise.all([
        fetch('/api/authority-engineer/extract/frameworks', { credentials: 'include' }),
        fetch('/api/authority-engineer/content/types', { credentials: 'include' }),
        fetch('/api/authority-engineer/content/list', { credentials: 'include' }),
        fetch('/api/authority-engineer/content/cta-options', { credentials: 'include' })
      ]);

      if (!frameworksRes.ok || !typesRes.ok || !contentRes.ok) {
        console.error('API errors:', {
          frameworks: frameworksRes.status,
          types: typesRes.status,
          content: contentRes.status
        });
        throw new Error('One or more API calls failed');
      }

      const frameworksData = await frameworksRes.json();
      const typesData = await typesRes.json();
      const contentData = await contentRes.json();
      const ctaData = ctaRes.ok ? await ctaRes.json() : { options: [] };

      setFrameworks(frameworksData.frameworks || []);
      setContentTypes(typesData.content_types || {});
      setContentPieces(contentData.content_pieces || []);
      setCtaOptions(ctaData.options || []);
      // Default to engagement CTA
      if (ctaData.options?.length > 0) {
        setSelectedCta(ctaData.options[0]);
      }
      setLoading(false);
    } catch (err) {
      console.error('Load data error:', err);
      setError('Failed to load data');
      setLoading(false);
    }
  };

  const startNewContent = () => {
    if (frameworks.length === 0) {
      setError('You need to extract some IP first before creating content. Go to IP Extraction to get started.');
      return;
    }
    setView('select-ip');
    setSelectedFramework(null);
    setSelectedType('');
    setIdeas([]);
    setSelectedIdea(null);
  };

  const generateIdeas = async () => {
    if (!selectedFramework || !selectedType) {
      setError('Please select both an IP framework and content type');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/content/generate-ideas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          framework_id: selectedFramework.id,
          content_type: selectedType
        })
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Failed to generate ideas');
      setIdeas(data.ideas || []);
      setView('ideas');
    } catch (err) {
      setError(err.message || 'Failed to generate ideas');
    } finally {
      setGenerating(false);
    }
  };

  const selectIdea = (idea) => {
    setSelectedIdea(idea);
    setView('generate');
  };

  const generateContent = async () => {
    setGenerating(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/content/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          framework_id: selectedFramework.id,
          content_type: selectedType,
          idea: selectedIdea,
          cta_target: selectedCta
        })
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Failed to generate content');
      const newContent = data.content;
      setContentPieces([newContent, ...contentPieces]);
      setSelectedContent(newContent);
      setView('edit');
      setSuccess('Content generated successfully!');
    } catch (err) {
      setError(err.message || 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const loadContentDetail = async (content) => {
    try {
      const response = await fetch(`/api/authority-engineer/content/${content.id}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setSelectedContent(data.content);
      setEditedContent(data.content);
      setView('edit');
    } catch (err) {
      setError('Failed to load content');
    }
  };

  const saveContent = async () => {
    setGenerating(true);
    try {
      // Convert hashtags input string to JSON array
      const hashtagsArray = hashtagsInput
        .split(/\s+/)
        .map(t => t.replace('#', '').trim())
        .filter(t => t);

      const dataToSave = {
        ...editedContent,
        hashtags: JSON.stringify(hashtagsArray)
      };

      await fetch(`/api/authority-engineer/content/${selectedContent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(dataToSave)
      });

      // Update in list
      setContentPieces(contentPieces.map(c =>
        c.id === selectedContent.id ? { ...c, ...dataToSave } : c
      ));
      setSelectedContent({ ...selectedContent, ...dataToSave });
      setEditMode(false);
      setSuccess('Content saved!');
    } catch (err) {
      setError('Failed to save content');
    } finally {
      setGenerating(false);
    }
  };

  const regenerateContent = async () => {
    if (!regenerateFeedback.trim()) {
      setError('Please provide feedback for regeneration');
      return;
    }

    // Make sure at least one option is selected
    if (!regenOptions.title && !regenOptions.hook && !regenOptions.content && !regenOptions.cta) {
      setError('Please select at least one part to regenerate');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const response = await fetch(`/api/authority-engineer/content/${selectedContent.id}/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          feedback: regenerateFeedback,
          regenerate: regenOptions
        })
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Failed to regenerate');
      const updated = data.content;
      setSelectedContent(updated);
      setEditedContent(updated);
      setContentPieces(contentPieces.map(c =>
        c.id === updated.id ? updated : c
      ));
      setRegenFeedback('');
      setActiveTab('content');
      setSuccess('Content regenerated!');
    } catch (err) {
      setError(err.message || 'Failed to regenerate');
    } finally {
      setGenerating(false);
    }
  };

  const repurposeContent = async () => {
    if (!repurposeType) {
      setError('Please select a content type to repurpose to');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const response = await fetch(`/api/authority-engineer/content/${selectedContent.id}/repurpose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ target_type: repurposeType })
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Failed to repurpose');
      const newContent = data.content;
      setContentPieces([newContent, ...contentPieces]);
      setSelectedContent(newContent);
      setEditedContent(newContent);
      setShowRepurpose(false);
      setRepurposeType('');
      setSuccess(`Content repurposed to ${contentTypes[repurposeType]?.label || repurposeType}!`);
    } catch (err) {
      setError(err.message || 'Failed to repurpose');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      const response = await fetch(`/api/authority-engineer/content/${selectedContent.id}/copy`, {
        credentials: 'include'
      });
      const data = await response.json();

      // Add title to content
      const contentWithTitle = `# ${data.title}\n\n${data.formatted_content}`;

      // Convert markdown to HTML for rich text copying
      const html = marked(contentWithTitle, { breaks: true, gfm: true });

      // Also create plain text version (strip markdown syntax)
      const plainText = contentWithTitle
        .replace(/\*\*(.*?)\*\*/g, '$1')  // Bold
        .replace(/\*(.*?)\*/g, '$1')       // Italic
        .replace(/^#{1,6}\s+/gm, '')       // Headers
        .replace(/^[-*+]\s+/gm, '• ')      // Bullet points
        .replace(/^\d+\.\s+/gm, '')        // Numbered lists
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); // Links

      // Try to copy as rich text (HTML) for proper formatting in Google Docs, blogs, etc.
      try {
        const blob = new Blob([html], { type: 'text/html' });
        const textBlob = new Blob([plainText], { type: 'text/plain' });
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': blob,
            'text/plain': textBlob
          })
        ]);
      } catch {
        // Fallback to plain text if rich text copy fails
        await navigator.clipboard.writeText(plainText);
      }

      setSuccess('Content copied! Paste into your blog or document.');
    } catch (err) {
      setError('Failed to copy content');
    }
  };

  const deleteContent = async (id) => {
    if (!window.confirm('Delete this content piece?')) return;

    try {
      await fetch(`/api/authority-engineer/content/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      setContentPieces(contentPieces.filter(c => c.id !== id));
      if (selectedContent?.id === id) {
        setSelectedContent(null);
        setView('library');
      }
      setSuccess('Content deleted');
    } catch (err) {
      setError('Failed to delete content');
    }
  };

  // Social posting functions
  const openPostModal = async () => {
    setShowPostModal(true);
    setLoadingAccounts(true);
    setSelectedAccount(null);

    // Prepare the post content - combine hook + content + cta
    let content = '';
    if (selectedContent.hook) content += selectedContent.hook + '\n\n';
    if (selectedContent.content) {
      // Strip markdown for social post
      const plainContent = selectedContent.content
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/\*(.*?)\*/g, '$1')
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/^[-*+]\s+/gm, '• ')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
      content += plainContent + '\n\n';
    }
    if (selectedContent.cta) content += selectedContent.cta;

    // Add hashtags if present
    if (selectedContent.hashtags && selectedContent.hashtags !== '[]') {
      try {
        const tags = JSON.parse(selectedContent.hashtags);
        if (Array.isArray(tags) && tags.length > 0) {
          content += '\n\n' + tags.map(t => t.startsWith('#') ? t : `#${t}`).join(' ');
        }
      } catch {
        // Ignore parse errors
      }
    }

    setPostContent(content.trim());

    try {
      const response = await fetch('/api/social/accounts', { credentials: 'include' });
      const data = await response.json();

      if (response.ok && data.accounts) {
        setSocialAccounts(data.accounts);
        if (data.accounts.length > 0) {
          setSelectedAccount(data.accounts[0]);
        }
      } else {
        setError(data.error || 'Failed to load social accounts');
      }
    } catch (err) {
      setError('Failed to load social accounts');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const postToFacebook = async () => {
    if (!selectedAccount) {
      setError('Please select an account to post to');
      return;
    }

    setPosting(true);
    setError('');

    try {
      const response = await fetch('/api/social/post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          content: postContent,
          account_id: selectedAccount.id || selectedAccount.pageId,
          platform: 'facebook',
          content_id: selectedContent.id
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Posted to Facebook successfully!');
        setShowPostModal(false);
        // Update content status locally
        setSelectedContent({ ...selectedContent, status: 'published' });
        setContentPieces(contentPieces.map(c =>
          c.id === selectedContent.id ? { ...c, status: 'published' } : c
        ));
      } else {
        setError(data.error || 'Failed to post to Facebook');
      }
    } catch (err) {
      setError('Failed to post to Facebook');
    } finally {
      setPosting(false);
    }
  };

  const getTypeLabel = (type) => {
    if (!type) return 'Unknown';
    return contentTypes[type]?.label || type.replace(/_/g, ' ');
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: 'secondary',
      ready: 'primary',
      published: 'success',
      repurposed: 'info'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status || 'draft'}</Badge>;
  };

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading Content Matrix...</p>
      </Container>
    );
  }

  // Library View
  const renderLibrary = () => (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Content Matrix</h2>
          <p className="text-muted mb-0">Transform your IP into platform-ready content</p>
        </div>
        <Button variant="primary" onClick={startNewContent}>
          + Create New Content
        </Button>
      </div>

      {contentPieces.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <h4>No Content Yet</h4>
            <p className="text-muted">
              Start by selecting an IP framework and content type to generate your first piece.
            </p>
            <Button variant="primary" onClick={startNewContent}>
              Create Your First Content
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {contentPieces.map(content => (
            <Col md={6} lg={4} key={content.id} className="mb-3">
              <Card className="h-100" style={{ cursor: 'pointer' }} onClick={() => loadContentDetail(content)}>
                <Card.Body>
                  <div className="d-flex justify-content-between mb-2">
                    <Badge bg="info">{getTypeLabel(content.content_type)}</Badge>
                    {getStatusBadge(content.status)}
                  </div>
                  <Card.Title className="h6">{content.title}</Card.Title>
                  {content.hook && (
                    <Card.Text className="small text-muted" style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {content.hook}
                    </Card.Text>
                  )}
                  <div className="mt-2">
                    <small className="text-muted">
                      {content.word_count} words • {new Date(content.created_at).toLocaleDateString()}
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </>
  );

  // Select IP View
  const renderSelectIP = () => (
    <>
      <Button variant="link" className="mb-3 p-0" onClick={() => setView('library')}>
        ← Back to Library
      </Button>

      <h3>Create New Content</h3>
      <p className="text-muted">Select your IP framework and the type of content you want to create.</p>

      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>1. Select IP Framework</Card.Header>
            <Card.Body style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <ListGroup variant="flush">
                {frameworks.map(fw => (
                  <ListGroup.Item
                    key={fw.id}
                    action
                    active={selectedFramework?.id === fw.id}
                    onClick={() => setSelectedFramework(fw)}
                  >
                    <strong>{fw.name}</strong>
                    <br />
                    <small className="text-muted">{fw.framework_type}</small>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>2. Select Content Type</Card.Header>
            <Card.Body>
              {Object.entries(contentTypes).map(([key, type]) => (
                <Form.Check
                  key={key}
                  type="radio"
                  id={`type-${key}`}
                  name="contentType"
                  label={
                    <span>
                      <strong>{type.label}</strong>
                      <br />
                      <small className="text-muted">
                        {type.word_range[0]}-{type.word_range[1]} words • {type.platform}
                      </small>
                    </span>
                  }
                  checked={selectedType === key}
                  onChange={() => setSelectedType(key)}
                  className="mb-3"
                />
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="text-center">
        <Button
          variant="primary"
          size="lg"
          onClick={generateIdeas}
          disabled={!selectedFramework || !selectedType || generating}
        >
          {generating ? (
            <>
              <Spinner size="sm" className="me-2" />
              Generating Ideas...
            </>
          ) : (
            'Generate Content Ideas →'
          )}
        </Button>
      </div>
    </>
  );

  // Ideas View
  const renderIdeas = () => (
    <>
      <Button variant="link" className="mb-3 p-0" onClick={() => setView('select-ip')}>
        ← Back to Selection
      </Button>

      <h3>Choose Your Content Angle</h3>
      <p className="text-muted">
        Based on <strong>{selectedFramework?.name}</strong>, here are 5 {getTypeLabel(selectedType)} ideas:
      </p>

      <Row>
        {ideas.map((idea, idx) => (
          <Col md={6} key={idx} className="mb-3">
            <Card
              className="h-100"
              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
              onClick={() => selectIdea(idea)}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'none'}
            >
              <Card.Body>
                <h5>{idea.title}</h5>
                <p className="text-muted mb-2">{idea.hook}</p>
                <Badge bg="secondary">{idea.angle}</Badge>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <div className="text-center mt-3">
        <Button variant="outline-secondary" onClick={generateIdeas} disabled={generating}>
          {generating ? 'Generating...' : 'Generate 5 More Ideas'}
        </Button>
      </div>
    </>
  );

  // Generate Confirmation View
  const renderGenerate = () => (
    <>
      <Button variant="link" className="mb-3 p-0" onClick={() => setView('ideas')}>
        ← Back to Ideas
      </Button>

      <Card className="mb-4">
        <Card.Body className="text-center py-5">
          <h3>Ready to Generate</h3>
          <p className="text-muted mb-4">
            You're about to create a {getTypeLabel(selectedType)} based on:
          </p>

          <Card className="mb-4 mx-auto" style={{ maxWidth: '500px' }}>
            <Card.Body className="text-start">
              <h5>{selectedIdea.title}</h5>
              <p className="text-muted mb-2">{selectedIdea.hook}</p>
              <Badge bg="secondary">{selectedIdea.angle}</Badge>
            </Card.Body>
          </Card>

          {/* CTA Target Selector */}
          {ctaOptions.length > 0 && (
            <Form.Group className="mb-4 mx-auto" style={{ maxWidth: '500px', textAlign: 'left' }}>
              <Form.Label><strong>Call-to-Action Target</strong></Form.Label>
              <Form.Select
                value={selectedCta?.id || ''}
                onChange={(e) => {
                  const cta = ctaOptions.find(opt => opt.id === e.target.value);
                  setSelectedCta(cta);
                }}
              >
                {ctaOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>
                    {opt.type === 'engagement' ? opt.name : `${opt.type.replace('_', ' ').toUpperCase()}: ${opt.name}`}
                  </option>
                ))}
              </Form.Select>
              {selectedCta && selectedCta.description && (
                <Form.Text className="text-muted">
                  {selectedCta.description.substring(0, 100)}{selectedCta.description.length > 100 ? '...' : ''}
                </Form.Text>
              )}
            </Form.Group>
          )}

          <Button
            variant="primary"
            size="lg"
            onClick={generateContent}
            disabled={generating}
          >
            {generating ? (
              <>
                <Spinner size="sm" className="me-2" />
                Generating Content...
              </>
            ) : (
              'Generate Full Content'
            )}
          </Button>
        </Card.Body>
      </Card>
    </>
  );

  // Edit/View Content
  const renderEdit = () => (
    <>
      <Button variant="link" className="mb-3 p-0" onClick={() => { setView('library'); setSelectedContent(null); }}>
        ← Back to Library
      </Button>

      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <Badge bg="info" className="me-2">{getTypeLabel(selectedContent.content_type)}</Badge>
          {getStatusBadge(selectedContent.status)}
          <h3 className="mt-2">{selectedContent.title}</h3>
        </div>
        <div>
          <Button variant="outline-primary" className="me-2" onClick={copyToClipboard}>
            Copy
          </Button>
          <Button variant="primary" className="me-2" onClick={openPostModal}>
            Post to Facebook
          </Button>
          <Button variant="outline-info" className="me-2" onClick={() => setShowRepurpose(true)}>
            Repurpose
          </Button>
          <Button
            variant={editMode ? 'success' : 'outline-secondary'}
            className="me-2"
            onClick={() => {
              if (editMode) {
                saveContent();
              } else {
                setEditedContent({ ...selectedContent });
                // Initialize hashtags input string
                try {
                  const tags = typeof selectedContent.hashtags === 'string'
                    ? JSON.parse(selectedContent.hashtags)
                    : selectedContent.hashtags;
                  setHashtagsInput(Array.isArray(tags) ? tags.map(t => `#${t.replace('#', '')}`).join(' ') : '');
                } catch {
                  setHashtagsInput(selectedContent.hashtags || '');
                }
                setEditMode(true);
              }
            }}
          >
            {editMode ? 'Save' : 'Edit'}
          </Button>
          <Button variant="outline-danger" onClick={() => deleteContent(selectedContent.id)}>
            Delete
          </Button>
        </div>
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        <Tab eventKey="content" title="Content">
          <Card>
            <Card.Body>
              {editMode ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      value={editedContent.title || ''}
                      onChange={(e) => setEditedContent({ ...editedContent, title: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Hook</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={editedContent.hook || ''}
                      onChange={(e) => setEditedContent({ ...editedContent, hook: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Content</Form.Label>
                    <RichTextEditor
                      key={selectedContent?.id || 'new'}
                      value={editedContent.content || ''}
                      onChange={(markdown) => setEditedContent({ ...editedContent, content: markdown })}
                      placeholder="Write your content here..."
                      height="300px"
                      toolbar="full"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Call to Action</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={editedContent.cta || ''}
                      onChange={(e) => setEditedContent({ ...editedContent, cta: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Hashtags</Form.Label>
                    <Form.Control
                      value={hashtagsInput}
                      onChange={(e) => setHashtagsInput(e.target.value)}
                      placeholder="#Marketing #Growth #Strategy"
                    />
                    <Form.Text className="text-muted">
                      Space-separated hashtags (with or without #)
                    </Form.Text>
                  </Form.Group>
                </>
              ) : (
                <>
                  {selectedContent.hook && (
                    <div className="mb-4">
                      <h6 className="text-muted">Hook</h6>
                      <p className="lead">{selectedContent.hook}</p>
                    </div>
                  )}

                  <div className="mb-4">
                    <h6 className="text-muted">Content</h6>
                    <div className="markdown-content">
                      <ReactMarkdown>{selectedContent.content}</ReactMarkdown>
                    </div>
                  </div>

                  {selectedContent.cta && (
                    <div className="mb-4">
                      <h6 className="text-muted">Call to Action</h6>
                      <p>{selectedContent.cta}</p>
                    </div>
                  )}

                  {selectedContent.hashtags && selectedContent.hashtags !== '[]' && (
                    <div>
                      <h6 className="text-muted">Hashtags</h6>
                      <p>
                        {(() => {
                          try {
                            const tags = JSON.parse(selectedContent.hashtags);
                            return Array.isArray(tags) && tags.length > 0
                              ? tags.map(t => t.startsWith('#') ? t : `#${t}`).join(' ')
                              : null;
                          } catch {
                            return selectedContent.hashtags;
                          }
                        })()}
                      </p>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="regenerate" title="Regenerate">
          <Card>
            <Card.Body>
              <h5>Want changes?</h5>
              <p className="text-muted">
                Select which parts to regenerate and describe what you'd like different.
              </p>

              <Form.Group className="mb-3">
                <Form.Label>What to Regenerate</Form.Label>
                <div className="d-flex gap-3 flex-wrap">
                  <Form.Check
                    type="checkbox"
                    id="regen-title"
                    label="Title"
                    checked={regenOptions.title}
                    onChange={(e) => setRegenOptions({ ...regenOptions, title: e.target.checked })}
                  />
                  <Form.Check
                    type="checkbox"
                    id="regen-hook"
                    label="Hook"
                    checked={regenOptions.hook}
                    onChange={(e) => setRegenOptions({ ...regenOptions, hook: e.target.checked })}
                  />
                  <Form.Check
                    type="checkbox"
                    id="regen-content"
                    label="Content"
                    checked={regenOptions.content}
                    onChange={(e) => setRegenOptions({ ...regenOptions, content: e.target.checked })}
                  />
                  <Form.Check
                    type="checkbox"
                    id="regen-cta"
                    label="Call to Action"
                    checked={regenOptions.cta}
                    onChange={(e) => setRegenOptions({ ...regenOptions, cta: e.target.checked })}
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Your Feedback</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="e.g., Make it more conversational, add a story at the beginning, focus more on the problem..."
                  value={regenerateFeedback}
                  onChange={(e) => setRegenFeedback(e.target.value)}
                />
              </Form.Group>

              <Button
                variant="primary"
                onClick={regenerateContent}
                disabled={generating || !regenerateFeedback.trim()}
              >
                {generating ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Regenerating...
                  </>
                ) : (
                  'Regenerate Content'
                )}
              </Button>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="details" title="Details">
          <Card>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p><strong>Content Type:</strong> {getTypeLabel(selectedContent.content_type)}</p>
                  <p><strong>Platform:</strong> {selectedContent.platform}</p>
                  <p><strong>Word Count:</strong> {selectedContent.word_count}</p>
                  <p><strong>Status:</strong> {selectedContent.status}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Angle:</strong> {selectedContent.angle}</p>
                  <p><strong>Target Audience:</strong> {selectedContent.target_audience}</p>
                  <p><strong>Created:</strong> {new Date(selectedContent.created_at).toLocaleString()}</p>
                </Col>
              </Row>

              {selectedContent.key_message && (
                <div className="mt-3">
                  <strong>Key Message:</strong>
                  <p>{selectedContent.key_message}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </>
  );

  return (
    <Container className="py-4">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {view === 'library' && renderLibrary()}
      {view === 'select-ip' && renderSelectIP()}
      {view === 'ideas' && renderIdeas()}
      {view === 'generate' && renderGenerate()}
      {view === 'edit' && selectedContent && renderEdit()}

      {/* Repurpose Modal */}
      <Modal show={showRepurpose} onHide={() => setShowRepurpose(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Repurpose Content</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Transform this {getTypeLabel(selectedContent?.content_type)} into:</p>

          <Form.Group>
            {Object.entries(contentTypes)
              .filter(([key]) => key !== selectedContent?.content_type)
              .map(([key, type]) => (
                <Form.Check
                  key={key}
                  type="radio"
                  id={`repurpose-${key}`}
                  name="repurposeType"
                  label={`${type.label} (${type.word_range[0]}-${type.word_range[1]} words)`}
                  checked={repurposeType === key}
                  onChange={() => setRepurposeType(key)}
                  className="mb-2"
                />
              ))}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRepurpose(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={repurposeContent}
            disabled={!repurposeType || generating}
          >
            {generating ? 'Repurposing...' : 'Repurpose'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Post to Facebook Modal */}
      <Modal show={showPostModal} onHide={() => setShowPostModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Post to Facebook</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingAccounts ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading connected accounts...</p>
            </div>
          ) : socialAccounts.length === 0 ? (
            <Alert variant="warning">
              <strong>No Facebook accounts connected.</strong>
              <p className="mb-0 mt-2">
                Connect your Facebook Page in your GoHighLevel dashboard under
                Settings &gt; Integrations &gt; Facebook to enable social posting.
              </p>
            </Alert>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label><strong>Select Facebook Page</strong></Form.Label>
                <Form.Select
                  value={selectedAccount?.id || selectedAccount?.pageId || ''}
                  onChange={(e) => {
                    const account = socialAccounts.find(a => (a.id || a.pageId) === e.target.value);
                    setSelectedAccount(account);
                  }}
                >
                  {socialAccounts.map((account, idx) => (
                    <option key={idx} value={account.id || account.pageId}>
                      {account.name || account.pageName || `Page ${idx + 1}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Post Content</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  value={postContent}
                  onChange={(e) => setPostContent(e.target.value)}
                />
                <Form.Text className="text-muted">
                  {postContent.length} characters
                </Form.Text>
              </Form.Group>

              <Alert variant="info">
                <small>
                  <strong>Note:</strong> This will post immediately to Facebook.
                  Scheduling will be available in a future update.
                </small>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPostModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={postToFacebook}
            disabled={posting || loadingAccounts || socialAccounts.length === 0}
          >
            {posting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Posting...
              </>
            ) : (
              'Post Now'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ContentMatrix;
